import React from "react";

import type { Size2D } from "@lib/utils/geometry";
import { elementIsVisible } from "@lib/utils/htmlElementUtils";

export function useElementSize(ref: React.RefObject<HTMLElement>): Size2D {
    const [size, setSize] = React.useState<Size2D>({
        width: ref.current?.offsetWidth ?? 0,
        height: ref.current?.offsetHeight ?? 0,
    });

    React.useEffect(
        function observeSizeEffect() {
            let isHidden = false;
            let currentSize = { width: 0, height: 0 };
            const handleResize = (): void => {
                let newSize: Size2D = { width: 0, height: 0 };
                if (ref.current) {
                    // If element is not visible do not change size as it might be expensive to render
                    if (!elementIsVisible(ref.current)) {
                        isHidden = true;
                        return;
                    }

                    newSize = {
                        width: ref.current.offsetWidth,
                        height: ref.current.offsetHeight,
                    };

                    if (isHidden && currentSize.width === newSize.width && currentSize.height === newSize.height) {
                        isHidden = false;
                        return;
                    }

                    currentSize = newSize;
                }

                // State is tracking DOM size, so we accept set-state here
                // eslint-disable-next-line @eslint-react/set-state-in-effect
                setSize(newSize);
            };

            const resizeObserver = new ResizeObserver(handleResize);

            if (ref.current) {
                handleResize();
                resizeObserver.observe(ref.current);
            }

            return () => {
                resizeObserver.disconnect();
            };
        },
        [ref],
    );

    return size;
}
